// src/pages/settings/SystemTab.tsx — onglet Système (mode maintenance + sauvegardes, super-admin uniquement)

import { useState, useEffect } from "react";
import type { ConfirmModalProps } from "../../components/ConfirmModal";
import { BtnDelete } from "../../components/IconButtons";
import {
  getMaintenanceStatus, setMaintenanceMode,
  type MaintenanceStatus,
} from "../../api/maintenanceApi";
import {
  listBackups, triggerBackup, restoreBackup, deleteBackup,
  type BackupFile,
} from "../../api/backupApi";

export function SystemTab({ setModal, showToast, onMaintenanceChange }:{
  setModal: (m: ConfirmModalProps | null) => void;
  showToast: (msg: string) => void;
  onMaintenanceChange?: (active: boolean) => void;
}) {
  const [maintenance,        setMaintenance]        = useState<MaintenanceStatus|null>(null);
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);
  const [maintenanceError,   setMaintenanceError]   = useState<string|null>(null);

  const [backups,          setBackups]          = useState<BackupFile[]>([]);
  const [backupsLoading,   setBackupsLoading]   = useState(false);
  const [backupTriggering, setBackupTriggering] = useState(false);
  const [backupRestoring,  setBackupRestoring]  = useState<string|null>(null); // nom fichier en cours
  const [backupDeleting,   setBackupDeleting]   = useState<string|null>(null);
  const [backupError,      setBackupError]      = useState<string|null>(null);

  useEffect(() => {
    setMaintenanceLoading(true); setMaintenanceError(null);
    getMaintenanceStatus()
      .then(setMaintenance)
      .catch(e => setMaintenanceError((e as Error).message))
      .finally(() => setMaintenanceLoading(false));
    setBackupsLoading(true); setBackupError(null);
    listBackups()
      .then(setBackups)
      .catch(e => setBackupError((e as Error).message))
      .finally(() => setBackupsLoading(false));
  }, []);

  const handleTriggerBackup = async () => {
    setBackupTriggering(true); setBackupError(null);
    try {
      const { fileName } = await triggerBackup();
      const updated = await listBackups();
      setBackups(updated);
      showToast(`Sauvegarde créée : ${fileName}`);
    } catch (e) { setBackupError((e as Error).message); }
    finally { setBackupTriggering(false); }
  };

  const handleDeleteBackup = (fileName: string) => {
    // Extrait le timestamp du nom : report_backup_YYYY-MM-DD_HHmmss.zip
    const timestamp = fileName.replace(/^report_backup_/, "").replace(/\.zip$/, "");
    setModal({
      title: "Supprimer cette sauvegarde ?",
      message: `Le fichier ${fileName} sera définitivement supprimé. Cette action est irréversible.`,
      confirmLabel: "Supprimer", danger: true,
      onCancel: () => setModal(null),
      onConfirm: async () => {
        setModal(null);
        setBackupDeleting(fileName); setBackupError(null);
        try {
          await deleteBackup(timestamp);
          setBackups(prev => prev.filter(b => b.name !== fileName));
          showToast("Sauvegarde supprimée.");
        } catch (e) { setBackupError((e as Error).message); }
        finally { setBackupDeleting(null); }
      },
    });
  };

  const handleRestoreBackup = (fileName: string) => {
    setModal({
      title: "Restaurer la base de données ?",
      message: `Cette opération va effacer intégralement la base de données puis la remplacer par le contenu de ${fileName}. Action irréversible.`,
      confirmLabel: "Restaurer", danger: true,
      onCancel: () => setModal(null),
      onConfirm: async () => {
        setModal(null);
        setBackupRestoring(fileName); setBackupError(null);
        try {
          const { counts } = await restoreBackup(fileName);
          const summary = Object.entries(counts).map(([t, n]) => `${t} : ${n}`).join(" · ");
          showToast(`Restauration terminée — ${summary}`);
        } catch (e) { setBackupError((e as Error).message); }
        finally { setBackupRestoring(null); }
      },
    });
  };

  const handleToggleMaintenance = () => {
    if (!maintenance) return;
    if (!maintenance.active) {
      setModal({
        title: "Activer le mode maintenance ?",
        message: "Tous les utilisateurs connectés (sauf vous) verront une page de maintenance et ne pourront plus accéder à l'application.",
        confirmLabel: "Activer", danger: true,
        onCancel: () => setModal(null),
        onConfirm: async () => {
          setModal(null);
          try {
            const s = await setMaintenanceMode(true);
            setMaintenance(s);
            onMaintenanceChange?.(true);
            showToast("Mode maintenance activé ✓");
          } catch (e: unknown) { showToast("Erreur : " + (e as Error).message); }
        },
      });
    } else {
      setModal({
        title: "Désactiver le mode maintenance ?",
        message: "L'application redeviendra accessible à tous les utilisateurs.",
        confirmLabel: "Désactiver", danger: false,
        onCancel: () => setModal(null),
        onConfirm: async () => {
          setModal(null);
          try {
            const s = await setMaintenanceMode(false);
            setMaintenance(s);
            onMaintenanceChange?.(false);
            showToast("Mode maintenance désactivé ✓");
          } catch (e: unknown) { showToast("Erreur : " + (e as Error).message); }
        },
      });
    }
  };

  return (
    <div style={{height:"100%",display:"flex",flexDirection:"column"}}>
      <h2 style={{fontSize:15,fontWeight:700,marginBottom:20,color:"var(--text-primary)",flexShrink:0}}>
        Administration système
      </h2>
      <div className="card" style={{padding:"20px 24px",flexShrink:0}}>
        <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:24}}>
          <div style={{flex:1}}>
            <div style={{fontSize:14,fontWeight:700,color:"var(--text-primary)",marginBottom:6,display:"flex",alignItems:"center",gap:8}}>
              <span>🔧</span> Mode maintenance
            </div>
            <div style={{fontSize:12,color:"var(--text-muted)",lineHeight:1.65,maxWidth:440}}>
              Lorsqu'il est actif, tous les utilisateurs connectés (sauf vous) voient une page de maintenance
              et ne peuvent plus naviguer dans l'application.
            </div>
            {maintenance?.active && maintenance.since && (
              <div style={{fontSize:11,color:"var(--red)",marginTop:10,fontWeight:600}}>
                ● Actif depuis le {new Date(maintenance.since).toLocaleString("fr-FR")}
              </div>
            )}
            {!maintenance?.active && maintenance !== null && (
              <div style={{fontSize:11,color:"var(--green-dim)",marginTop:10,fontWeight:600}}>
                ● Application accessible normalement
              </div>
            )}
            {maintenanceError && (
              <div style={{fontSize:11,color:"var(--red)",marginTop:10}}>⚠ {maintenanceError}</div>
            )}
          </div>
          <div style={{flexShrink:0}}>
            {maintenanceLoading ? (
              <span style={{fontSize:12,color:"var(--text-faint)"}}>Chargement…</span>
            ) : (
              <button
                onClick={handleToggleMaintenance}
                disabled={!maintenance}
                className="btn btn-sm"
                style={maintenance?.active
                  ? {background:"var(--green-dim)",color:"#fff",border:"none"}
                  : {background:"var(--red)",color:"#fff",border:"none"}
                }>
                {maintenance?.active ? "✓ Désactiver la maintenance" : "Activer la maintenance"}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Sauvegardes */}
      <div className="card" style={{marginTop:16,flex:1,minHeight:0,display:"flex",flexDirection:"column",padding:"20px 24px"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:16,marginBottom:16,flexShrink:0}}>
          <div>
            <div style={{fontSize:14,fontWeight:700,color:"var(--text-primary)",marginBottom:4,display:"flex",alignItems:"center",gap:8}}>
              <span>💾</span> Sauvegardes automatiques
            </div>
            <div style={{fontSize:12,color:"var(--text-muted)"}}>
              Export complet quotidien à 4h00 · Rétention 30 jours
            </div>
            {backupError && <div style={{fontSize:11,color:"var(--red)",marginTop:8}}>⚠ {backupError}</div>}
          </div>
          <button
            onClick={handleTriggerBackup}
            disabled={backupTriggering}
            className="btn btn-sm"
            style={{flexShrink:0,background:"var(--green-dim)",color:"#fff",border:"none"}}>
            {backupTriggering ? "Export…" : "Déclencher maintenant"}
          </button>
        </div>

        {backupsLoading
          ? <div style={{fontSize:12,color:"var(--text-faint)"}}>Chargement…</div>
          : backups.length === 0
            ? <div style={{fontSize:12,color:"var(--text-faint)"}}>Aucune sauvegarde disponible.</div>
            : <div style={{display:"flex",flexDirection:"column",gap:0,flex:1,minHeight:0,overflowY:"auto",scrollbarColor:"var(--green-dim) transparent"}}>
                {backups.map((b, i) => (
                  <div key={b.name} style={{
                    display:"flex",alignItems:"center",gap:12,
                    padding:"9px 0",
                    borderBottom: i < backups.length - 1 ? "1px solid #f0f0f0" : "none",
                  }}>
                    <span style={{fontSize:15,flexShrink:0}}>🗜</span>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:12,color:"var(--text-primary)",fontWeight:500}}>{b.name}</div>
                      <div style={{fontSize:11,color:"var(--text-faint)"}}>
                        {new Date(b.date).toLocaleString("fr-FR")} · {(b.size / 1024).toFixed(0)} Ko
                      </div>
                    </div>
                    <button
                      onClick={() => handleRestoreBackup(b.name)}
                      disabled={backupRestoring !== null || backupDeleting !== null}
                      className="btn btn-ghost btn-sm"
                      style={{flexShrink:0}}>
                      {backupRestoring === b.name ? "Restauration…" : "Restaurer"}
                    </button>
                    <BtnDelete
                      onClick={() => handleDeleteBackup(b.name)}
                      disabled={backupDeleting !== null || backupRestoring !== null}
                      title="Supprimer cette sauvegarde"/>
                  </div>
                ))}
              </div>
        }
      </div>
    </div>
  );
}
